import React, { useEffect, useRef, useState, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import {
  formatConnectionString,
  formatDatasetSubtitle,
  formatDatasetSegment,
  compileTemplateString,
  formatPath,
  AttributeSensitivity,
  Asset,
  AssetName,
  getAssetSubtitle,
  iconClassName,
} from '../../helpers';
import { SkeletonLoader, textareaToPlainText, Icon, Photo } from '../';
import { useTooltip } from '../../hooks';

import './SearchResult.css';

const _SearchResult = (
  { data, loadMore, resultCount, loading, searchTerm, containerElement = document, onClick },
  ref
) => {
  const refLoadMore = useRef();
  const { showTooltip, hideTooltip } = useTooltip();

  const [loadMoreData, setLoadMoreData] = useState(false);

  useEffect(() => {
    const element = containerElement;
    if (element) {
      element.addEventListener('scroll', trackScrolling);
      return () => {
        element.removeEventListener('scroll', trackScrolling);
      };
    }
  }, [containerElement]);

  useEffect(() => {
    if (loadMoreData) {
      loadMore();
    }
  }, [loadMoreData]);

  useEffect(() => {
    setLoadMoreData(prev => {
      return false;
    });
  }, [data]);

  const isVisible = el => {
    return el.getBoundingClientRect().top <= window.innerHeight;
  };

  const trackScrolling = () => {
    const loadingElement = refLoadMore.current;

    if (loadingElement && isVisible(loadingElement)) {
      setLoadMoreData(prev => {
        return true;
      });
    }
  };

  const renderLink = result => {
    switch (result.object_name) {
      case 'user':
        return compileTemplateString('/users/:user_id', result.searchable);

      case 'user_group':
        return compileTemplateString('/user_groups/:user_group_id', result.searchable);

      case 'attribute':
        return compileTemplateString('/attributes/:attribute_id', result.searchable);

      case 'attribute_value':
        return compileTemplateString(
          'attributes/:attribute_id/:attribute_value_id',
          result.searchable
        );

      case 'field':
        return compileTemplateString(
          '/systems/:system_id/:datasource_id/:dataset_group_id/:dataset_id/:field_id',
          result.searchable
        );

      case 'dataset':
        return compileTemplateString(
          '/systems/:system_id/:datasource_id/:dataset_group_id/:dataset_id',
          result.searchable
        );

      case 'dataset_group':
        return compileTemplateString(
          '/systems/:system_id/:datasource_id/:dataset_group_id',
          result.searchable
        );

      case 'datasource':
        return compileTemplateString('/systems/:system_id/:datasource_id', result.searchable);

      case 'system':
        return compileTemplateString('/systems/:system_id', result.searchable);

      case 'entity':
        return compileTemplateString('/glossaries/:glossary_id/:entity_id', result.searchable);

      case 'glossary':
        return compileTemplateString('/glossaries/:glossary_id', result.searchable);

      default:
        return '/';
    }
  };

  const renderTitle = result => {
    switch (result.object_name) {
      case 'user':
        return result.searchable['user_fullname'];
      case 'dataset':
        if (result.searchable['dataset_subtype_name']) {
          return formatDatasetSegment(
            result.searchable.dataset_name,
            result.searchable.dataset_subtype_name,
            { datasetSubtypeOperation: result.searchable.dataset_subtype_operation, asPill: true }
          );
        }
      // falls through to the default name rendering when the dataset has no API method
      default:
        return <React.Fragment>{result.searchable[result.object_name + '_name']}</React.Fragment>;
    }
  };

  /**
   * The secondary line under a search result's name, picked per asset type.
   *
   * Each type answers "which one is this?" with whatever distinguishes it: a username for a user, a
   * connection string for a datasource, the classification for a system or a glossary, the ancestor
   * path for a hierarchical asset. Types with nothing useful to add return null and show no subtitle.
   *
   * @param {{object_name: string, searchable: object}} result - One indexed row; `searchable` is the
   *   asset's `v_<object_name>` row as stored in the search index.
   * @returns {React.ReactNode|string|null}
   */
  const renderSubtitle = result => {
    switch (result.object_name) {
      case 'user':
        return result.searchable.user_username;

      case 'attribute':
        return getAssetSubtitle(result.searchable, Asset.Attribute);

      case 'attribute_value':
        // An attribute value has nothing to describe it beyond being one, which the type chip already
        // says. Where it sits — under which attribute — is the path row's job.
        return 'Dimension Attribute Value';

      case 'field':
        // The indexer bakes the full dataset ancestor chain into the search payload as
        // `ancestors`; the Field view mirror appends the field name as the leaf.
        return formatDatasetSubtitle(result.searchable, {
          ancestors: result.searchable.ancestors || [],
          trailingParts: [result.searchable.field_name],
        });

      case 'dataset':
        return formatDatasetSubtitle(result.searchable, {
          ancestors: result.searchable.ancestors || [],
        });

      case 'dataset_group':
        return formatConnectionString(
          result.searchable.datasource_database,
          result.searchable.datasource_hostname,
          [result.searchable.dataset_group_name],
          result.searchable.datasource_type_code,
          result.searchable.datasource_type_category
        );

      case 'datasource':
        return formatConnectionString(
          result.searchable.datasource_database,
          result.searchable.datasource_hostname,
          [],
          result.searchable.datasource_type_code,
          result.searchable.datasource_type_category
        );

      case 'system':
        const systemCode =
          result.searchable.system_code && result.searchable.system_code !== 'null'
            ? result.searchable.system_code
            : '';
        const systemType =
          result.searchable.system_type && result.searchable.system_type !== 'null'
            ? result.searchable.system_type
            : '';
        const delimiter = systemCode && systemType ? ' | ' : '';
        return systemCode + delimiter + systemType;

      case 'entity':
        return getAssetSubtitle(result.searchable, Asset.Entity);

      case 'glossary':
        // The indexed payload holds the string 'null' rather than a JSON null for an unset column.
        return result.searchable.glossary_type && result.searchable.glossary_type !== 'null'
          ? result.searchable.glossary_type
          : null;

      default:
        return null;
    }
  };

  const renderIcon = result => {
    switch (result.object_name) {
      case 'attribute':
        return (
          <div
            className={'icon icon-' + iconClassName(result.searchable.attribute_role_name)}
            onMouseEnter={() => showTooltip({ title: result.searchable.attribute_role_name })}
            onMouseLeave={() => hideTooltip()}
          ></div>
        );

      case 'attribute_value':
        return (
          <div
            className="icon icon-attribute-value"
            onMouseEnter={() => showTooltip({ title: AssetName[Asset.AttributeValue] })}
            onMouseLeave={() => hideTooltip()}
          ></div>
        );

      case 'entity':
        return (
          <div
            className={'icon icon-entity'}
            onMouseEnter={() => showTooltip({ title: 'Entity' })}
            onMouseLeave={() => hideTooltip()}
          ></div>
        );

      case 'field':
        return (
          <div
            className={'icon icon-' + iconClassName(result.searchable.datatype_category)}
            onMouseEnter={() => showTooltip({ title: result.searchable.datatype_fullname })}
            onMouseLeave={() => hideTooltip()}
          ></div>
        );

      case 'dataset':
        return (
          <div
            className={'icon icon-' + iconClassName(result.searchable.dataset_type_name)}
            onMouseEnter={() => showTooltip({ title: result.searchable.dataset_type_name })}
            onMouseLeave={() => hideTooltip()}
          ></div>
        );

      case 'datasource':
        return (
          <div
            className={'icon icon-' + iconClassName(result.searchable.datasource_type_category)}
            onMouseEnter={() => showTooltip({ title: result.searchable.datasource_type_name })}
            onMouseLeave={() => hideTooltip()}
          ></div>
        );

      default:
        return null;
    }
  };

  const renderBody = result => {
    switch (result.object_name) {
      case 'user':
        return `${result.searchable['user_title'] || ''}${
          result.searchable['user_title'] && result.searchable['user_department'] ? ' @ ' : ''
        }${result.searchable['user_department'] || ''}`;

      case 'field':
        return textareaToPlainText(result.searchable['field_description']);

      default:
        return textareaToPlainText(result.searchable[result.object_name + '_description']);
    }
  };

  const renderPhoto = result => {
    let name;
    let photo;
    switch (result.object_name) {
      case 'user':
        name = result.searchable.user_fullname;
        photo = result.searchable.user_photo;
        break;
      case 'user_group':
        name = result.searchable.user_group_name;
        photo = result.searchable.user_group_photo;
        break;

      default:
      // Nothing
    }

    return <Photo label={name} name={name} data={photo} />;
  };

  const renderType = result => result.object_name.toUpperCase().replaceAll('_', ' ');

  /**
   * Where a result sits, as the containers above it — so a name on its own is never ambiguous. Two
   * glossaries can hold an attribute of the same name, two systems a dataset of the same name, and the
   * same name can appear at different depths.
   *
   * The asset itself is left out: its name is already the title above. What counts as a container
   * depends on the family:
   *
   *  - glossary assets: the glossary, then the entities above the asset, then the attribute for a value;
   *  - system assets: the system, datasource, dataset group and datasets above the asset.
   *
   * This is Katalogue's own hierarchy, the one the breadcrumb trail shows, and it is deliberately not
   * the same thing as the subtitle of a system asset — that is the address in the source system
   * ("katalogue.public.term.glossary_id"), which names a database rather than a datasource and never
   * mentions the system at all.
   *
   * Both families read their chain from the `ancestors` array the indexer bakes into the row: entities
   * above a glossary asset, parent datasets above a system asset. Glossary rows indexed before that
   * existed fall back to the immediate parent until the search index is rebuilt.
   *
   * @param {object} result - A search result row.
   * @returns {string|null} The path, or null for an asset type that has no hierarchy above it.
   */
  const renderPath = result => {
    const searchable = result.searchable;
    const ancestors = Array.isArray(searchable.ancestors) ? searchable.ancestors : null;

    switch (result.object_name) {
      case Asset.Entity:
      case Asset.Attribute:
      case Asset.AttributeValue: {
        const chain = ancestors
          ? ancestors.map(ancestor => ancestor.entity_name)
          : [searchable.parent_entity_name].filter(Boolean);

        // A value hangs under its attribute, which is the last container above it.
        const parts = [searchable.glossary_name, ...chain];
        if (result.object_name === Asset.AttributeValue) {
          parts.push(searchable.attribute_name);
        }

        return formatPath(parts.filter(Boolean), ' > ');
      }

      case Asset.Datasource:
      case Asset.DatasetGroup:
      case Asset.Dataset:
      case Asset.Field: {
        const isDatasource = result.object_name === Asset.Datasource;
        const belowGroup =
          result.object_name === Asset.Dataset || result.object_name === Asset.Field;

        // A dataset can nest, so the datasets above this one are containers too. For a field they are
        // the ancestors of its own dataset, which is then a container in its own right. API datasets
        // carry their method, as they do everywhere else they are named.
        const datasetChain = belowGroup
          ? (ancestors || []).map(ancestor =>
              formatDatasetSegment(ancestor.dataset_name, ancestor.dataset_subtype_name)
            )
          : [];

        const parts = [
          searchable.system_name,
          isDatasource ? null : searchable.datasource_name,
          belowGroup ? searchable.dataset_group_name : null,
          ...datasetChain,
          result.object_name === Asset.Field
            ? formatDatasetSegment(searchable.dataset_name, searchable.dataset_subtype_name)
            : null,
        ];

        return formatPath(parts.filter(Boolean), ' > ');
      }

      // A system is the root of its hierarchy, and the remaining types have none.
      default:
        return null;
    }
  };

  const setRef = (el, key) => {
    if (ref) {
      ref.current[key] = el;
    }
  };

  return (
    <div className="SearchResult">
      {!resultCount && !loading && (
        <div className="no-results">No assets found that matches "{searchTerm}"</div>
      )}

      {data.map((result, key) => {
        return (
          <Link
            key={key}
            to={renderLink(result)}
            tabIndex={0}
            className="search-result-link"
            ref={el => setRef(el, key)}
            onClick={onClick}
          >
            <div className="search-result">
              <div className={'search-result-type'}>{renderType(result)}</div>
              <div className="search-result-container">
                {['user', 'user_group'].includes(result.object_name) && (
                  <div className="search-result-photo">{renderPhoto(result)}</div>
                )}
                <div className="search-result-content">
                  {renderPath(result) && (
                    <div className="search-result-path">{renderPath(result)}</div>
                  )}
                  <div className="search-result-title">{renderTitle(result)}</div>
                  <div className="search-result-subtitle">
                    {renderIcon(result)}
                    <div className="connection_string">{renderSubtitle(result)}</div>
                    {result.searchable.attribute_sensitivity_name && (
                      <React.Fragment>
                        {result.searchable.is_pii && (
                          <Icon
                            name="pii"
                            tooltip="Classified as Personally Identifiable Information (PII)"
                          />
                        )}
                        <Icon
                          name="attribute_sensitivity"
                          label={result.searchable.attribute_sensitivity_name}
                          value={result.searchable.attribute_sensitivity_level}
                          values={AttributeSensitivity}
                        />
                      </React.Fragment>
                    )}
                  </div>

                  <div className="search-result-body">{renderBody(result)}</div>
                </div>
              </div>
            </div>
          </Link>
        );
      })}
      {((loading && data.length === 0) || data.length < resultCount) && (
        <div
          className="search-result"
          ref={refLoadMore}
          onClick={() =>
            setLoadMoreData(prev => {
              return true;
            })
          }
          title="Click to load more assets"
        >
          <div className="search-result-type">
            <SkeletonLoader width="10vw" />
          </div>
          <div className="search-result-title">
            <SkeletonLoader width="20vw" />
          </div>
          <div className="search-result-subtitle">
            <div className="connection_string">
              <SkeletonLoader width="15vw" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const SearchResult = forwardRef(_SearchResult);
